import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import cn from 'classnames';

import { useCurrentEnvironment } from '../../hooks/contexts';

import { getLogoByEnvName } from '../../utils/utils';

import { RoutePath, RouteTo } from '../../routes';

import styles from './Nav.module.scss';

export enum NavigationTab {
    Packs = 'packs',
    Carousels = 'carousels',
    Portal = 'portal',
    ShopTabs = 'shoptabs',
    Maps = 'maps',
    Gifts = 'gifts',
    Medias = 'medias',
    Accounts = 'accounts',
    GroupedProfiles = 'grouped-profiles',
    Versions = 'versions',
    InAppEvents = 'iaes',
    Tools = 'tools',
    PaApple = 'price-automation-apple',
    PaGoogle = 'price-automation-google',
    SkuManagement = 'sku-management',
    Banners = 'banners',
    PacksDrawer = 'packs-drawer',
    Activations = 'activations',
    Tags = 'tags',
    Events = 'events',
    Users = 'users',
    EmailAudit = 'email-audit',
}

export function getTabByRoute(routeName: keyof typeof RoutePath) {
    switch (routeName) {
        case 'Packs':
        case 'PackEdit':
        case 'PackNew':
            return NavigationTab.Packs;

        case 'Carousels':
        case 'CarouselEdit':
        case 'CarouselNew':
            return NavigationTab.Carousels;

        case 'Tabs':
        case 'TabEdit':
        case 'TabNew':
            return NavigationTab.ShopTabs;

        case 'Tags':
        case 'TagEdit':
        case 'TagNew':
            return NavigationTab.Tags;

        case 'PacksDrawer':
        case 'PacksDrawerEdit':
        case 'PacksDrawerNew':
            return NavigationTab.PacksDrawer;

        case 'Gifts':
        case 'GiftEdit':
        case 'GiftNew':
        case 'GiftsV2':
        case 'GiftsV2Planner':
        case 'GiftsV2Scheduler':
            return NavigationTab.Gifts;

        case 'Maps':
        case 'MapEdit':
            return NavigationTab.Maps;

        case 'Tools':
            return NavigationTab.Tools;

        case 'Versions':
            return NavigationTab.Versions;

        case 'InAppEvents':
        case 'InAppEventsEdit':
        case 'InAppEventsNew':
            return NavigationTab.InAppEvents;

        case 'Medias':
            return NavigationTab.Medias;

        case 'Banners':
            return NavigationTab.Banners;

        case 'AccountManagement':
        case 'GroupedProfiles':
            return NavigationTab.Accounts;

        case 'PriceAutomationApple':
            return NavigationTab.PaApple;
        case 'PriceAutomationGoogle':
            return NavigationTab.PaGoogle;

        case 'SKUManagement':
            return NavigationTab.SkuManagement;

        case 'PortalLibrary':
            return NavigationTab.Portal;
        case 'PortalPublication':
            return NavigationTab.Portal;

        case 'Activations':
            return NavigationTab.Activations;

        case 'Events':
            return NavigationTab.Events;

        case 'UserManagement':
            return NavigationTab.Users;

        case 'EmailAuditList':
        case 'EmailAuditItem':
            return NavigationTab.EmailAudit;

        default:
            return undefined;
    }
}

type NavProps = {
    tab?: NavigationTab;
    isEventsEnabled: boolean;
    onTabChange: (e: MouseEvent<HTMLAnchorElement>, tab: NavigationTab) => void;
    isUserManagementEnabled: boolean;
    children: ReactNode;
};

const Nav = (props: NavProps) => {
    const {
        onTabChange,
        tab,
        isEventsEnabled,
        isUserManagementEnabled,
        children,
    } = props;
    const [margin, setMargin] = useState<number>(100);
    const navRef = useRef<HTMLElement>(null);
    const { currentEnvName } = useCurrentEnvironment();

    const getStatusClass = (linkName: NavigationTab) =>
        tab === linkName ? styles.active : '';

    useEffect(() => {
        const onWindowResize = () => {
            const marginAfterNav = 25;
            const { height } = navRef.current?.getBoundingClientRect() || {};

            if (!height) {
                return;
            }

            setMargin(height + marginAfterNav);
        };

        // To set the margin after initial mount
        onWindowResize();

        // To adjust the margin if the window resized
        window.addEventListener('resize', onWindowResize, { passive: true });

        return () => {
            window.removeEventListener('resize', onWindowResize);
        };
    }, []);

    return (
        <>
            <nav
                key={tab}
                className={cn(styles.navigation, 'blurrable')}
                ref={navRef}
            >
                <div className={styles.linksWrapper}>
                    <Link
                        onClick={(e) =>
                            onTabChange(e, NavigationTab.Activations)
                        }
                        to={RouteTo.Activations()}
                        className={styles.logoWrapperLink}
                    >
                        <img
                            alt="tocaboca_logo"
                            className={styles.logo}
                            src={getLogoByEnvName(currentEnvName)}
                        />
                    </Link>
                    <Link
                        onClick={(e) =>
                            onTabChange(e, NavigationTab.Activations)
                        }
                        className={cn(
                            getStatusClass(NavigationTab.Activations),
                        )}
                        to={RouteTo.Activations()}
                    >
                        Activations
                    </Link>
                    <div className={styles.submenuWrapper}>
                        <button type="button" className={styles.submenuButton}>
                            Shop <span style={{ fontSize: 11 }}>▼</span>
                        </button>
                        <div className={styles.submenu}>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.Packs)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.Packs),
                                )}
                                to={RouteTo.Packs()}
                            >
                                Packs
                            </Link>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.Carousels)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.Carousels),
                                )}
                                to={RouteTo.Carousels()}
                            >
                                Carousels
                            </Link>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.ShopTabs)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.ShopTabs),
                                )}
                                to={RouteTo.Tabs()}
                            >
                                Tabs
                            </Link>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.Maps)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.Maps),
                                )}
                                to={RouteTo.Maps()}
                            >
                                Maps
                            </Link>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.Tags)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.Tags),
                                )}
                                to={RouteTo.Tags()}
                            >
                                Tags
                            </Link>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.PacksDrawer)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.PacksDrawer),
                                )}
                                to={RouteTo.PacksDrawer()}
                            >
                                Packs Drawer
                            </Link>
                        </div>
                    </div>

                    <div className={styles.submenuWrapper}>
                        <button type="button" className={styles.submenuButton}>
                            Gifts <span style={{ fontSize: 11 }}>▼</span>
                        </button>
                        <div className={styles.submenu}>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.Gifts)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.Gifts),
                                )}
                                to={RouteTo.Gifts()}
                            >
                                Gifts
                            </Link>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.Gifts)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.Gifts),
                                )}
                                to={RouteTo.GiftsV2Scheduler()}
                            >
                                Gifts V2 (WIP)
                            </Link>
                        </div>
                    </div>
                    <Link
                        onClick={(e) => onTabChange(e, NavigationTab.Banners)}
                        className={cn(getStatusClass(NavigationTab.Banners))}
                        to={RouteTo.Banners()}
                    >
                        Banners
                    </Link>
                    {isEventsEnabled && (
                        <Link
                            onClick={(e) =>
                                onTabChange(e, NavigationTab.Events)
                            }
                            className={cn(getStatusClass(NavigationTab.Events))}
                            to={RouteTo.Events()}
                        >
                            Events
                        </Link>
                    )}
                    <Link
                        onClick={(e) => onTabChange(e, NavigationTab.Portal)}
                        className={cn(getStatusClass(NavigationTab.Portal))}
                        to={RouteTo.PortalLibrary()}
                    >
                        Portal
                    </Link>
                    <div className={styles.submenuWrapper}>
                        <button type="button" className={styles.submenuButton}>
                            Accounts <span style={{ fontSize: 11 }}>▼</span>
                        </button>
                        <div className={styles.submenu}>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.Accounts)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.Accounts),
                                )}
                                to={RouteTo.AccountManagement()}
                            >
                                Account management
                            </Link>
                            <Link
                                onClick={(e) =>
                                    onTabChange(
                                        e,
                                        NavigationTab.GroupedProfiles,
                                    )
                                }
                                className={cn(
                                    getStatusClass(
                                        NavigationTab.GroupedProfiles,
                                    ),
                                )}
                                to={RouteTo.GroupedProfiles()}
                            >
                                Groups management
                            </Link>
                        </div>
                    </div>
                    <div className={styles.submenuWrapper}>
                        <button type="button" className={styles.submenuButton}>
                            Price Automation{' '}
                            <span style={{ fontSize: 11 }}>▼</span>
                        </button>
                        <div className={styles.submenu}>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.PaApple)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.PaApple),
                                )}
                                to={RouteTo.PriceAutomationApple()}
                            >
                                Apple Connect
                            </Link>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.PaGoogle)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.PaGoogle),
                                )}
                                to={RouteTo.PriceAutomationGoogle()}
                            >
                                Google Play
                            </Link>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.SkuManagement)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.SkuManagement),
                                )}
                                to={RouteTo.SKUManagement()}
                            >
                                SKU Management
                            </Link>
                        </div>
                    </div>
                    <div className={styles.submenuWrapper}>
                        <button type="button" className={styles.submenuButton}>
                            More <span style={{ fontSize: 11 }}>▼</span>
                        </button>
                        <div className={styles.submenu}>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.Versions)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.Versions),
                                )}
                                to={RouteTo.Versions()}
                            >
                                Release management
                            </Link>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.Medias)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.Medias),
                                )}
                                to={RouteTo.Medias()}
                            >
                                Media Library
                            </Link>
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.InAppEvents)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.InAppEvents),
                                )}
                                to={RouteTo.InAppEvents()}
                            >
                                In-App Events
                            </Link>
                            {isUserManagementEnabled && (
                                <Link
                                    onClick={(e) =>
                                        onTabChange(e, NavigationTab.Users)
                                    }
                                    className={cn(
                                        getStatusClass(NavigationTab.Users),
                                    )}
                                    to={RouteTo.UserManagement()}
                                >
                                    Users
                                </Link>
                            )}
                            <Link
                                onClick={(e) =>
                                    onTabChange(e, NavigationTab.EmailAudit)
                                }
                                className={cn(
                                    getStatusClass(NavigationTab.EmailAudit),
                                )}
                                to={RouteTo.EmailAuditList()}
                            >
                                Email Audit
                            </Link>
                        </div>
                    </div>
                </div>
                <div className={styles.childrenWrapper}>
                    <button
                        type="button"
                        onClick={() => {
                            document.cookie =
                                'experimental-client=true; path=/';
                            window.location.reload();
                        }}
                        style={{
                            background: 'none',
                            border: '1px solid currentColor',
                            borderRadius: 4,
                            padding: '4px 10px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            fontSize: '0.85em',
                        }}
                    >
                        Experimental
                    </button>
                    {children}
                </div>
            </nav>
            <div style={{ marginBottom: margin }} />
        </>
    );
};

export default Nav;
